import { styled } from "goober";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { connectorTypeLabel } from "../lib/connector";
import { playClick } from "../lib/sounds";
import { Button } from "./Button";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { Text } from "./Text";

type App = ConnectorWithOAuth;

const Container = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  &[data-clickable="true"] {
    cursor: pointer;
    &:hover {
      background: var(--alpha4);
    }
  }
  & .row-actions {
    margin-left: auto;
    opacity: 0;
  }
  &:hover .row-actions {
    opacity: 1;
  }
`;

const EmptyIcon = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  color: var(--text-primary);
`;

const AppInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

type Props = {
  apps: readonly App[];
  onSelect?: (app: App) => void;
  emptyDescription?: string;
  removableIds?: ReadonlySet<string>;
  onRemove?: (app: App) => void;
};

export function ConnectorsList({
  apps,
  onSelect,
  emptyDescription = "Connectors available in this context appear here.",
  removableIds,
  onRemove,
}: Props) {
  if (apps.length === 0) {
    return (
      <Container>
        <Row>
          <EmptyIcon>
            <Icon name="circle-slash" size={16} />
          </EmptyIcon>
          <AppInfo>
            <Text variant="text-regular-md" color="var(--text-primary)">
              No connectors
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {emptyDescription}
            </Text>
          </AppInfo>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      {apps.map((app) => (
        <Row
          key={app.id}
          data-clickable={onSelect ? "true" : undefined}
          onMouseDown={
            onSelect
              ? () => {
                  playClick();
                  onSelect(app);
                }
              : undefined
          }
        >
          <ConnectorLogoContainer
            size={32}
            borderRadius={8}
            outline={false}
            hoverBackground={false}
          >
            <ConnectorLogo app={app} size={16} />
          </ConnectorLogoContainer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 0,
            }}
          >
            <Text
              ellipsis
              variant="text-regular-md"
              color="var(--text-primary)"
            >
              {app.name}
            </Text>
            <Text variant="text-small" color="var(--text-secondary)" ellipsis>
              {connectorTypeLabel(app)}
            </Text>
          </div>
          {onRemove && removableIds?.has(app.id) && (
            <div
              className="row-actions"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Dropdown
                opener={<Button variant="secondary" icon="more" />}
                align="right"
                minWidth={180}
                sections={[
                  {
                    items: [
                      {
                        type: "button" as const,
                        left: <Icon name="trash" size={16} />,
                        label: "Remove access",
                        onClick: () => onRemove(app),
                      },
                    ],
                  },
                ]}
              />
            </div>
          )}
        </Row>
      ))}
    </Container>
  );
}
